class EventMap {
  static CATEGORY_COLORS = {
    tävlingar: '#e74c3c',
    kvällsaktivitet: '#8e44ad',
    filmvisning: '#2980b9',
    release: '#27ae60',
    workshop: '#f39c12',
    demodagar: '#2251c8',
    'after ski': '#d35400',
    community: '#1f8b13',
  };

  static DEFAULT_COLOR = 'rgb(var(--color-foreground))';

  static ADDRESS_FALLBACK_COORDINATES = {
    'sankt olavs vag 37, 837 52 are': { lat: 63.3996, lng: 13.0807 },
    'arevagen 115, 83752, are': { lat: 63.3982, lng: 13.0815 },
    'arevagen 115, 83752 are': { lat: 63.3982, lng: 13.0815 },
    'sjungarvagen, 780 68 transtrand': { lat: 61.0323, lng: 13.2854 },
    'hotell riksgransen, riksgransvagen 15, 981 94 riksgransen': { lat: 68.4273, lng: 18.1247 },
    '7586+hf undersaker': { lat: 63.3142, lng: 13.2356 },
    'kallviksbacken, 791 23 falun': { lat: 60.6078, lng: 15.6382 },
    'talmantie 341, 04240 sipoo, finland': { lat: 60.4011, lng: 25.3605 },
    'husa liftvagen 10, 837 94 jarpen': { lat: 63.2817, lng: 13.1199 },
    'granberget leksand, 793 97 siljansnas': { lat: 60.819, lng: 14.7761 },
    '925 93 hemavan': { lat: 65.8077, lng: 15.0896 },
    'sunnerstabacken dag hammarskjolds vag 270, 756 52 uppsala': { lat: 59.8065, lng: 17.6319 },
    'skelleftea skidklubb mossgatan 31, 931 70 skelleftea': { lat: 64.7494, lng: 20.9528 },
  };

  constructor(root) {
    this.root = root;
    if (!this.root) return;

    this.apiEndpoint = '/api/2024-10/graphql.json';
    this.now = new Date();
    this.statusFilter = 'upcoming';
    this.categoryFilter = 'all';
    this.allEvents = [];
    this.categoryOptions = [];
    this.geocodeCache = this.loadGeocodeCache();
    this.geocodeNetworkDisabled = false;
    this.hasLoggedGeocodeNetworkFailure = false;
    this.leafletMap = null;
    this.markerLayer = null;
    this.filterSwipers = {};

    this.elements = {
      categoryFilters: this.root.querySelector('#emap-tag-filters'),
      statusFilters: this.root.querySelector('#emap-status-filters'),
      loading: this.root.querySelector('[data-event-map-loading]'),
      empty: this.root.querySelector('[data-event-map-empty]'),
      unresolved: this.root.querySelector('[data-event-map-unresolved]'),
      count: this.root.querySelector('[data-event-map-count]'),
      legend: this.root.querySelector('[data-event-map-legend]'),
      mapCanvas: this.root.querySelector('[data-event-map-canvas]'),
      selected: this.root.querySelector('[data-event-map-selected]'),
    };

    this.bindExternalViewEvents();
    this.init();
  }

  async init() {
    if (!window.L) {
      this.showLoading('Leaflet kunde inte laddas.');
      return;
    }

    this.showLoading('Laddar events...');

    try {
      this.allEvents = await this.fetchEvents();
      this.categoryOptions = this.collectCategories(this.allEvents);
      this.renderCategoryFilter();
      this.renderStatusFilter();
      await this.ensureCoordinates(this.allEvents);
      this.initializeMap();
      this.renderMap();
    } catch (error) {
      console.error('EventMap: failed to initialize', error);
      this.showLoading('Det gick inte att ladda eventkartan just nu.');
    }
  }

  renderCategoryFilter() {
    const container = this.elements.categoryFilters;
    const swiperWrapper = container?.querySelector('.board-finder__swiper-wrapper');
    if (!container || !swiperWrapper) return;

    swiperWrapper.innerHTML = [
      '<div class="swiper-slide selected" data-filter="all"><div class="board-style-slide"><span>Alla</span></div></div>',
      ...this.categoryOptions.map((category) => {
        const slug = this.categorySlug(category);
        return `<div class="swiper-slide" data-filter="${slug}" data-category="${this.escapeHtml(category)}"><div class="board-style-slide"><span>${this.escapeHtml(category)}</span></div></div>`;
      }),
    ].join('');

    this.setupFilterSlideClicks(container, (slide) => {
      const filter = slide.dataset.filter;
      this.categoryFilter = filter === 'all' ? 'all' : slide.dataset.category || 'all';
      this.renderMap();
    });

    this.initializeFilterSwiper(container, 'categories');
  }

  renderStatusFilter() {
    const container = this.elements.statusFilters;
    if (!container) return;

    this.setupFilterSlideClicks(container, (slide) => {
      this.statusFilter = slide.dataset.status || 'upcoming';
      this.renderMap();
    });

    this.initializeFilterSwiper(container, 'status');
  }

  setupFilterSlideClicks(container, onClick) {
    container.querySelectorAll('.swiper-slide').forEach((slide) => {
      slide.addEventListener('click', () => {
        container.querySelectorAll('.swiper-slide').forEach((item) => item.classList.remove('selected'));
        slide.classList.add('selected');
        onClick(slide);
      });
    });
  }

  initializeFilterSwiper(container, key) {
    if (!container) return;

    if (this.filterSwipers[key]) {
      this.filterSwipers[key].destroy(true, true);
      this.filterSwipers[key] = null;
    }
  }

  async fetchEvents() {
    const query = `
      query GetAllEventsForMap {
        metaobjects(type: "events", first: 250) {
          nodes {
            handle
            fields {
              key
              value
              reference {
                ... on MediaImage {
                  image {
                    url(transform: { maxWidth: 700 })
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(this.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.shopifyConfig?.storefrontAccessToken || '',
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.statusText}`);
    }

    const { data, errors } = await response.json();
    if (errors) {
      throw new Error(errors[0].message);
    }

    return data.metaobjects.nodes
      .map((node) => {
        const event = { handle: node.handle };
        node.fields.forEach((field) => {
          if (field.key === 'rubrik') event.title = field.value;
          if (field.key === 'starttid') event.startDate = new Date(field.value);
          if (field.key === 'sluttid') event.endDate = new Date(field.value);
          if (field.key === 'adress') event.address = field.value;
          if (field.key === 'kategori') event.categories = field.value ? JSON.parse(field.value) : [];
          if (field.key === 'info_beskrivning') event.descriptionRaw = field.value;
          if (field.key === 'lank') event.externalLink = field.value;
          if (field.key === 'huvudbild' && field.reference?.image) {
            event.image = field.reference.image.url;
            event.imageAlt = field.reference.image.altText || event.title || 'Eventbild';
          }
          if (field.key === 'latitude' || field.key === 'latitud' || field.key === 'lat') {
            event.latitude = field.value;
          }
          if (field.key === 'longitude' || field.key === 'longitud' || field.key === 'long' || field.key === 'lng') {
            event.longitude = field.value;
          }
        });
        const manualCoordinates = this.readManualCoordinates(event);
        if (manualCoordinates) {
          event.coordinates = manualCoordinates;
        }
        event.description = this.extractDescriptionText(event.descriptionRaw);
        return event;
      })
      .filter((event) => event.handle && event.title && event.startDate && event.endDate && (event.coordinates || event.address));
  }

  collectCategories(events) {
    const categories = new Set();
    events.forEach((event) => {
      (event.categories || []).forEach((category) => categories.add(category));
    });
    return [...categories].sort();
  }

  getFilteredEvents() {
    return this.allEvents.filter((event) => {
      if (this.categoryFilter !== 'all' && !(event.categories || []).includes(this.categoryFilter)) {
        return false;
      }

      const isPast = event.endDate < this.now;
      if (this.statusFilter === 'upcoming' && isPast) return false;
      if (this.statusFilter === 'past' && !isPast) return false;

      return true;
    });
  }

  async ensureCoordinates(events) {
    const uniqueAddresses = [
      ...new Set(
        events
          .filter((event) => !event.coordinates)
          .map((event) => this.normalizeAddress(event.address))
          .filter(Boolean),
      ),
    ];
    const unresolved = [];

    for (const address of uniqueAddresses) {
      if (this.geocodeCache[address]) continue;

      const fallback = this.getFallbackCoordinates(address);
      if (fallback) {
        this.geocodeCache[address] = fallback;
        this.saveGeocodeCache();
        continue;
      }

      if (this.geocodeNetworkDisabled) {
        unresolved.push(address);
        continue;
      }

      const coords = await this.geocodeAddress(address);
      if (coords) {
        this.geocodeCache[address] = coords;
        this.saveGeocodeCache();
      } else {
        unresolved.push(address);
      }

      if (!this.geocodeNetworkDisabled) {
        await this.delay(1100);
      }
    }

    events.forEach((event) => {
      if (event.coordinates) return;
      const normalizedAddress = this.normalizeAddress(event.address);
      if (normalizedAddress && this.geocodeCache[normalizedAddress]) {
        event.coordinates = this.geocodeCache[normalizedAddress];
      }
    });

    this.renderUnresolved(unresolved);
  }

  async geocodeAddress(address) {
    const endpoint = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(address)}`;
    try {
      const response = await fetch(endpoint, {
        headers: {
          'Accept-Language': 'sv,en',
        },
      });

      if (!response.ok) return null;
      const matches = await response.json();
      if (!Array.isArray(matches) || matches.length === 0) return null;

      return {
        lat: Number(matches[0].lat),
        lng: Number(matches[0].lon),
      };
    } catch (error) {
      if (!this.hasLoggedGeocodeNetworkFailure) {
        this.hasLoggedGeocodeNetworkFailure = true;
        this.geocodeNetworkDisabled = true;
        console.info('EventMap: external geocoding unavailable in this environment. Falling back to cached/manual coordinates only.');
      }
      return null;
    }
  }

  initializeMap() {
    if (!this.elements.mapCanvas) return;
    if (this.leafletMap) return;

    this.leafletMap = L.map(this.elements.mapCanvas, {
      scrollWheelZoom: true,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(this.leafletMap);

    this.markerLayer = L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        let sizeClass = 'small';
        if (count >= 10) sizeClass = 'medium';
        if (count >= 25) sizeClass = 'large';
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: `event-map__cluster event-map__cluster--${sizeClass}`,
          iconSize: L.point(40, 40),
        });
      },
    });

    this.markerLayer.on('clusterclick', (e) => {
      const events = e.layer
        .getAllChildMarkers()
        .map((m) => m._eventData)
        .filter(Boolean)
        .sort((a, b) => a.startDate - b.startDate);
      if (events.length) this.renderSelectedEvents(events);
    });

    this.leafletMap.addLayer(this.markerLayer);
  }

  bindExternalViewEvents() {
    document.addEventListener('events-view:show', (event) => {
      if (event.detail?.view !== 'map' || !this.leafletMap) return;
      this.refreshMapAfterTabShow();
    });
  }

  refreshMapAfterTabShow() {
    const refresh = () => {
      if (!this.leafletMap) return;
      this.leafletMap.invalidateSize();
      this.renderMap();
    };

    // First frame after tab becomes visible.
    window.requestAnimationFrame(() => {
      refresh();
      // One extra delayed pass for late layout settling/fonts/images.
      window.setTimeout(refresh, 180);
    });
  }

  renderMap() {
    if (!this.leafletMap || !this.markerLayer) return;

    const filtered = this.getFilteredEvents();
    const eventsWithCoordinates = filtered.filter((event) => event.coordinates);

    this.hideLoading();
    this.markerLayer.clearLayers();
    this.updateCount(eventsWithCoordinates.length, filtered.length);
    this.renderLegend(eventsWithCoordinates);

    if (eventsWithCoordinates.length === 0) {
      this.showEmpty('Inga event med kartposition matchar ditt filter.');
      return;
    }

    this.hideEmpty();

    const bounds = [];

    eventsWithCoordinates.forEach((event) => {
      const color = this.categoryColor(this.getPrimaryCategory(event));
      const marker = L.marker([event.coordinates.lat, event.coordinates.lng], {
        icon: L.divIcon({
          html: `<span style="background:${color}"></span>`,
          className: 'event-map__pin',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
      });

      marker._eventData = event;
      marker.bindPopup(this.renderPopup([event]));
      marker.on('click', () => this.renderSelectedEvents([event]));
      this.markerLayer.addLayer(marker);
      bounds.push([event.coordinates.lat, event.coordinates.lng]);
    });

    if (bounds.length === 1) {
      this.leafletMap.setView(bounds[0], 10);
    } else {
      this.leafletMap.fitBounds(bounds, { padding: [30, 30] });
    }

    if (eventsWithCoordinates.length > 0 && this.elements.selected && this.elements.selected.hidden) {
      this.renderSelectedEvents([eventsWithCoordinates[0]]);
    }
  }

  renderLegend(events) {
    const legendEl = this.elements.legend;
    if (!legendEl) return;

    const categories = new Map();
    events.forEach((event) => {
      const category = this.getPrimaryCategory(event);
      if (!category) return;
      const slug = this.categorySlug(category);
      if (!categories.has(slug)) {
        categories.set(slug, { label: category, color: this.categoryColor(category) });
      }
    });

    if (categories.size === 0) {
      legendEl.innerHTML = '';
      return;
    }

    legendEl.innerHTML = [...categories.values()]
      .sort((a, b) => a.label.localeCompare(b.label, 'sv'))
      .map(
        (item) =>
          `<span class="event-map__legend-item"><span class="event-map__legend-dot" style="background:${item.color}"></span>${this.escapeHtml(item.label)}</span>`,
      )
      .join('');
  }

  renderPopup(events) {
    const sortedEvents = [...events].sort((a, b) => a.startDate - b.startDate);

    if (sortedEvents.length === 1) {
      const event = sortedEvents[0];
      return `
        <div class="event-map__popup-card">
          <a class="event-map__popup-link event-map__popup-link--title" href="/pages/events/${event.handle}">
            ${this.escapeHtml(event.title)}
          </a>
          <div class="event-map__popup-date">${this.escapeHtml(this.formatPopupDate(event.startDate, event.endDate))}</div>
          ${event.image ? `<a href="/pages/events/${event.handle}" class="event-map__popup-image-link"><img class="event-map__popup-image" src="${this.escapeHtml(event.image)}" alt="${this.escapeHtml(event.imageAlt || event.title)}" loading="lazy"></a>` : ''}
        </div>
      `;
    }

    const items = sortedEvents
      .map((event) => {
        return `
          <li class="event-map__popup-item">
            <a class="event-map__popup-link event-map__popup-link--title" href="/pages/events/${event.handle}">
              ${this.escapeHtml(event.title)}
            </a>
            <div class="event-map__popup-date">${this.escapeHtml(this.formatPopupDate(event.startDate, event.endDate))}</div>
            ${event.image ? `<a href="/pages/events/${event.handle}" class="event-map__popup-image-link"><img class="event-map__popup-image" src="${this.escapeHtml(event.image)}" alt="${this.escapeHtml(event.imageAlt || event.title)}" loading="lazy"></a>` : ''}
          </li>
        `;
      })
      .join('');

    return `
      <div class="event-map__popup-card">
        <ul class="event-map__popup-list">${items}</ul>
      </div>
    `;
  }

  renderSelectedEvents(events) {
    const container = this.elements.selected;
    if (!container) return;
    if (!events || !events.length) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const sorted = [...events].sort((a, b) => a.startDate - b.startDate);
    const heading = sorted.length > 1 ? 'Event på vald plats' : 'Valt event';
    const cards = sorted
      .map((event) => {
        const categories = (event.categories || [])
          .map((category) => {
            const bg = this.categoryColor(category);
            const fg = this.contrastTextColor(bg);
            return `<span class="event-category-pill" style="background:${bg};color:${fg}">${this.escapeHtml(category)}</span>`;
          })
          .join('');
        const description = event.description ? this.escapeHtml(event.description) : '';
        const addressQuery = event.address ? encodeURIComponent(event.address) : '';
        return `
          <article class="event-map__selected-card event-detail">
            <div class="event-detail__media">
              ${event.image ? `<div class="event-detail__hero-image"><img class="event-map__selected-image" src="${this.escapeHtml(event.image)}" alt="${this.escapeHtml(event.imageAlt || event.title)}" loading="lazy"></div>` : ''}
            </div>
            <div class="event-detail__content">
              <a class="event-map__selected-title event-detail__title h3" href="/pages/events/${event.handle}">${this.escapeHtml(event.title)}</a>
              <div class="event-detail__date">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.8333 3.33334H4.16667C3.24619 3.33334 2.5 4.07953 2.5 5.00001V16.6667C2.5 17.5872 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5872 17.5 16.6667V5.00001C17.5 4.07953 16.7538 3.33334 15.8333 3.33334Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M13.3333 1.66666V5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M6.66667 1.66666V5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M2.5 8.33334H17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                <div>${this.escapeHtml(this.formatPopupDate(event.startDate, event.endDate))}</div>
              </div>
              ${description ? `<div class="event-detail__description rte"><p>${description}</p></div>` : ''}
              <div class="event-detail__divider"></div>
              ${event.address ? `<a href="https://www.google.com/maps/search/?api=1&query=${addressQuery}" target="_blank" rel="noopener noreferrer" class="event-detail__address"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.5 8.33334C17.5 14.1667 10 19.1667 10 19.1667C10 19.1667 2.5 14.1667 2.5 8.33334C2.5 6.34422 3.29018 4.43656 4.6967 3.03004C6.10322 1.62352 8.01088 0.833344 10 0.833344C11.9891 0.833344 13.8968 1.62352 15.3033 3.03004C16.7098 4.43656 17.5 6.34422 17.5 8.33334Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M10 10.8333C11.3807 10.8333 12.5 9.71406 12.5 8.33334C12.5 6.95263 11.3807 5.83334 10 5.83334C8.61929 5.83334 7.5 6.95263 7.5 8.33334C7.5 9.71406 8.61929 10.8333 10 10.8333Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg><div><strong>Plats:</strong><br>${this.escapeHtml(event.address)}</div></a>` : ''}
              ${categories ? `<div class="event-detail__categories">${categories}</div>` : ''}
              <div class="event-detail__actions">
                <a class="button button--primary" href="/pages/events/${event.handle}">Läs mer</a>
                ${event.externalLink ? `<a class="button button--secondary" href="${this.escapeHtml(event.externalLink)}" target="_blank" rel="noopener noreferrer">Extern länk</a>` : ''}
              </div>
            </div>
          </article>
        `;
      })
      .join('');

    container.innerHTML = `
      <h3 class="event-map__selected-heading h3">${heading}</h3>
      <div class="event-map__selected-grid">${cards}</div>
    `;
    container.hidden = false;
    container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  renderUnresolved(unresolvedAddresses) {
    const unresolvedEl = this.elements.unresolved;
    if (!unresolvedEl) return;

    if (!unresolvedAddresses.length) {
      unresolvedEl.innerHTML = '';
      return;
    }

    const items = unresolvedAddresses.slice(0, 8).map((address) => `<li>${this.escapeHtml(address)}</li>`).join('');
    const networkNote = this.geocodeNetworkDisabled
      ? '<div>Extern geokodning verkar blockerad i nuvarande miljö. Lägg lat/lng manuellt för full täckning.</div>'
      : '';

    unresolvedEl.innerHTML = `
      <strong>Adresser som inte kunde geokodas automatiskt:</strong>
      ${networkNote}
      <ul class="event-map__errors">${items}</ul>
    `;
  }

  showLoading(text) {
    if (!this.elements.loading) return;
    this.elements.loading.textContent = text;
    this.elements.loading.hidden = false;
  }

  hideLoading() {
    if (this.elements.loading) this.elements.loading.hidden = true;
  }

  showEmpty(text) {
    if (!this.elements.empty) return;
    this.elements.empty.textContent = text;
    this.elements.empty.hidden = false;
  }

  hideEmpty() {
    if (this.elements.empty) this.elements.empty.hidden = true;
  }

  updateCount(mappedCount, totalFiltered) {
    if (!this.elements.count) return;
    this.elements.count.textContent = `${mappedCount} av ${totalFiltered} filtrerade event visas på kartan`;
  }

  normalizeAddress(address) {
    if (!address) return '';
    const trimmed = address.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return '';
    return trimmed;
  }

  getPrimaryCategory(event) {
    const first = event.categories?.[0];
    return first ? String(first).trim() : '';
  }

  categorySlug(category) {
    return String(category || '').toLowerCase().trim();
  }

  categoryColor(category) {
    const slug = this.categorySlug(category);
    return EventMap.CATEGORY_COLORS[slug] || EventMap.DEFAULT_COLOR;
  }

  contrastTextColor(color) {
    const rgb = this.parseColorToRgb(color);
    if (!rgb) return '#fff';
    const [r, g, b] = rgb.map((channel) => channel / 255);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 0.63 ? '#111' : '#fff';
  }

  parseColorToRgb(color) {
    const value = String(color || '').trim();
    if (!value) return null;
    const hexMatch = value.match(/^#([a-fA-F0-9]{6})$/);
    if (hexMatch) {
      const hex = hexMatch[1];
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
    const rgbMatch = value.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
    if (rgbMatch) {
      return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
    }
    return null;
  }

  readManualCoordinates(event) {
    const lat = this.parseCoordinateValue(event.latitude);
    const lng = this.parseCoordinateValue(event.longitude);
    if (lat === null || lng === null) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  }

  parseCoordinateValue(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;

    const normalized = String(value)
      .trim()
      .replace(/\s+/g, '')
      .replace(',', '.')
      .replace(/[^\d.-]/g, '');

    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  getFallbackCoordinates(address) {
    const key = this.addressLookupKey(address);
    if (!key) return null;
    return EventMap.ADDRESS_FALLBACK_COORDINATES[key] || null;
  }

  addressLookupKey(address) {
    return String(address || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\./g, '')
      .trim();
  }

  loadGeocodeCache() {
    try {
      const raw = localStorage.getItem('event-map-geocode-v1');
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  }

  saveGeocodeCache() {
    try {
      localStorage.setItem('event-map-geocode-v1', JSON.stringify(this.geocodeCache));
    } catch (error) {
      console.warn('EventMap: failed to save geocode cache', error);
    }
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  formatPopupDate(startDate, endDate) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
    const dayNames = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];

    const sameDay = startDate.toDateString() === endDate.toDateString();
    if (sameDay) {
      return `${dayNames[startDate.getDay()]} ${startDate.getDate()} ${months[startDate.getMonth()]}`;
    }

    return `${startDate.getDate()} ${months[startDate.getMonth()]} - ${endDate.getDate()} ${months[endDate.getMonth()]}`;
  }

  extractDescriptionText(rawValue) {
    if (!rawValue) return '';

    const trimmed = String(rawValue).trim();
    if (!trimmed) return '';

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        const extracted = this.extractTextFromRichNode(parsed).trim();
        return this.truncateText(extracted, 340);
      } catch (error) {
        // Fall through to plain text handling.
      }
    }

    const plain = trimmed.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return this.truncateText(plain, 340);
  }

  extractTextFromRichNode(node) {
    if (!node) return '';
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map((child) => this.extractTextFromRichNode(child)).join(' ');
    if (typeof node !== 'object') return '';

    let text = '';
    if (typeof node.value === 'string') text += ` ${node.value}`;
    if (node.text && typeof node.text.value === 'string') text += ` ${node.text.value}`;
    if (Array.isArray(node.children)) text += ` ${node.children.map((child) => this.extractTextFromRichNode(child)).join(' ')}`;
    if (Array.isArray(node.content)) text += ` ${node.content.map((child) => this.extractTextFromRichNode(child)).join(' ')}`;
    return text.replace(/\s+/g, ' ').trim();
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 1).trimEnd()}...`;
  }

  delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }
}

function initEventMap() {
  const root = document.querySelector('[data-event-map]');
  if (!root) return;
  new EventMap(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEventMap);
} else {
  initEventMap();
}
